/* eslint-disable max-lines */
import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import {
  IconButton,
  Tooltip,
  HStack,
  VStack,
  Button,
  useDisclosure,
  Text,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import { CheckIcon } from '@chakra-ui/icons'
import cx from 'classnames'
import dayjs from 'dayjs'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import Highlighter from 'react-highlight-words'
import { toast } from 'react-toastify'
import { Col } from 'reactstrap'
import colors from 'theme/colors.theme'
import { UpdateBody } from 'types/common'
import { uploadFile } from 'API/cms'
import { addStepToCommonLibrary } from 'API/commonLibrary'
import { createMedia, updateMediaById } from 'API/media'
import { deleteStepById, updateStepById } from 'API/step'
import InlineTextField from 'components/InlineTextField'
import ModalConfirm from 'components/ModalConfirm'
import SvgIcon from 'components/SvgIcon'
import { EBreakPoint } from 'constants/theme'
import { AuthRoleIdEnum } from 'constants/user'
import { IMedia } from 'interfaces/media'
import { IStepWithRelations } from 'interfaces/step'
import { ITheme } from 'interfaces/theme'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import SelectIconModal from 'pages/IconBuilderPage/components/SelectIconModal'
import MediaManager, { MediaFileType, MEDIATYPES } from '../../../../../../MediaManager'
import { getRenderProcess } from '../../../../utils'
import ModalAddStep from './components/ModalAddStep'
import styles from './cardHeader.module.scss'

interface ICardHeaderProps {
  name?: string
  position?: number
  hideStepNumbers?: boolean
  onClick: () => void
  onStartEditing?: MouseEventHandler<any>
  onStopEditing?: MouseEventHandler<any>
  onToggleMessageView?: Function
  mediaList: IMedia[]
  step: IStepWithRelations
  isCommonStepFromStepId?: number
  isEditing: boolean
  lastStep: boolean
  procedureId: number
  className?: string
  searchText?: any
  handleShowAutosave: () => void
  canEditProcess: boolean
  isCollapsed: boolean
  needsReview?: boolean
  isStepLevelChange?: boolean
  stepLevelChangeType?: 'added' | 'modified' | 'reordered' | null
  isReviewMode?: boolean
  stepReviewStatus?: {needsReview: boolean; isReviewed: boolean}
  onToggleStepReview?: (stepId: number) => void
}

const CardHeader = ({
  name,
  position,
  hideStepNumbers = true,
  searchText,
  step,
  onClick,
  handleShowAutosave,
  onStartEditing = () => {},
  onStopEditing = () => {},
  onToggleMessageView = () => {},
  needsReview = false,
  isEditing,
  lastStep,
  procedureId,
  className,
  canEditProcess,
  isCollapsed,
  isStepLevelChange = false,
  stepLevelChangeType = null,
  isReviewMode = false,
  stepReviewStatus = {needsReview: false, isReviewed: false},
  onToggleStepReview = () => {},
  ...props
}: ICardHeaderProps) => {
  const { processStore, organizationStore, iconBuilderStore, authStore, commonLibraryStore } = useStores()
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}
  const [showManageMediaDialog, setShowManageMediaDialog] = useState(false)
  const [showModalAddStep, setShowModalAddStep] = useState(false)
  const [deleteStepId, setDeleteStepId] = useState(0)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isProcessArchived = processStore?.process?.archivedAt && processStore?.process?.archivedAt !== null
  const isManageMode = processStore?.isManageModeInDetail
  const { userDetail } = authStore
  const isBasicUser = userDetail.authRoleId === AuthRoleIdEnum.PLAYER
  const [isEditStepName, setIsEditStepName] = useState<boolean>(false)
  const isCommonStepFromStepId = commonLibraryStore?.isFeatureEnabled ? props.isCommonStepFromStepId : undefined
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  function handleDeleteStep(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    setDeleteStepId(step.id)
  }

  function handleRemoveCommonStep(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    isCommonStepFromStepId && setDeleteStepId(isCommonStepFromStepId)
  }

  async function handleDeleteStepConfirmed(): Promise<void> {
    await deleteStepById(deleteStepId)
    if (processStore.process) {
      setDeleteStepId(0)
      getRenderProcess(processStore.process.id, processStore)
      handleShowAutosave()
    }
  }

  async function uploadMedia(fileToUpload: File, setUploadProgress: (percent: number) => any) {
    const category: any = fileToUpload.type.split('/')[0]
    const fileType = [MediaFileType.IMAGE, MediaFileType.VIDEO].includes(category) ? category : MediaFileType.FILE
    const url = await uploadFile(
      fileType,
      fileToUpload,
      (data: any) => {
        setUploadProgress(Math.round((100 * data.loaded) / data.total))
      },
      // Org store may not be loaded on a direct detail visit — fall back to the
      // persisted active org so /cms/{type} gets its required organizationId.
      organizationStore.organization?.id ?? authStore.resolvedActiveOrgId ?? undefined
    )
    if (url) {
      const mediaBody: UpdateBody<IMedia> = {
        organizationId: organizationStore.organization?.id,
        position: props.mediaList.length + 1, // continue from latest position
        mediaType: MEDIATYPES[fileType].mediaType,
        stepId: step.id,
        createdAt: dayjs().toDate(),
        updatedAt: dayjs().toDate(),
        [MEDIATYPES[fileType].column]: url.split('/').pop(),
        originalFile: fileToUpload.name
      }

      await createMedia(mediaBody)
      //
      getRenderProcess(processStore.process.id, processStore)
    }
  }

  async function handleChangeCaption(mediaId: number, caption: string) {
    await updateMediaById(mediaId, { name: caption })
    //
    await getRenderProcess(processStore.process.id, processStore)
  }

  const contents: string = useMemo(() => {
    return Array.isArray(step?.textContents) ? step.textContents.map(content => content?.content ?? '').join(' ') : ''
  }, [step.textContents])

  const searchIndex: number = String(searchText)
    .toLowerCase()
    .split(' ')
    .reduce((result, currentValue) => {
      const index: number = contents.toLowerCase().indexOf(currentValue)
      if (index > -1 && (index < result || result < 0)) {
        return index
      }
      return result
    }, -1)

  const textToHighlight: string = useMemo(() => {
    const maxStart: number = [searchIndex - 10, searchIndex - 20, searchIndex - 30, searchIndex, 0].reduce(
      (result, currentValue) => {
        if (currentValue > 0 && currentValue < result && currentValue < searchIndex) {
          return currentValue
        }
        return result
      },
      searchIndex
    )
    const sliceStart: number = contents.slice(maxStart, searchIndex).indexOf(' ') + maxStart + 1
    const sliceEnd: number =
      contents.slice(sliceStart + Math.max(60, contents.length - sliceStart)).indexOf(' ') + sliceStart + 60
    //* INFO: replace any html tags that might be in the text

    return contents.slice(sliceStart, sliceEnd).replace(/<[^>]*>?/gm, ' ')
  }, [contents, searchIndex, searchText])

  async function onSelect(iconId: number): Promise<void> {
    await updateStepById(step.id, { iconId })
    getRenderProcess(processStore.process.id, processStore)
    onClose()
  }

  async function handleEditStepName(newTitle: string) {
    if (newTitle) {
      await updateStepById(step.id, {
        name: newTitle
      }).then(() => getRenderProcess(processStore.process.id, processStore))
    }
  }

  useEffect(() => {
    if (isEditing) {
      iconBuilderStore.fetchStepIconList()
    }
  }, [isEditing])

  const {needsReview: stepNeedsReview, isReviewed: stepIsReviewed} = stepReviewStatus

  const handleReviewButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (step.id) {
      onToggleStepReview(step.id)
    }
  }

  return (
    <Box position="relative" width="100%">
      <Col
        xl="12"
        lg="12"
        md="12"
        sm="12"
        xs="12"
        className={cx(
          styles.container,
          className,
          commonLibraryStore?.isFeatureEnabled && {
            [styles.commonStep]: !!isCommonStepFromStepId,
            [styles.mainCommonStep]: step?.commonLibrary && step?.commonLibrary?.stepId === step?.id
          }
        )}
        onClick={onClick}
        style={{
          backgroundColor: needsReview ? '#FEF2F2' : 'white'
        }}
      >
      <VStack width="full" align="flex-start" paddingX={{ base: 2, md: 0 }}>
        <HStack width="full">
          <div className={styles.layout}>
            <div className={styles.stepIcon}>
              {step.iconId && <IconBuilder icon={step.icon} size={32} isActive />}
              {isEditing && <SvgIcon iconName="edit-hover-button" className={styles.editButton} onClick={onOpen} />}
            </div>
            <div className={styles.label}>
              {!isMobile && (
                <Box
                  onClick={
                    isEditing
                      ? event => {
                          event.stopPropagation()
                          setIsEditStepName(true)
                        }
                      : undefined
                  }
                  flexDirection="row"
                  display="flex"
                >
                  {!hideStepNumbers && (
                    <Text margin={0} alignSelf="center">
                      {position}.
                    </Text>
                  )}
                  {isEditStepName && isEditing ? (
                    <InlineTextField
                      value={name}
                      onApplyChange={handleEditStepName}
                      editFirst
                      onStartEdit={() => setIsEditStepName(true)}
                      onStopEdit={() => setIsEditStepName(false)}
                    />
                  ) : (
                    <Text margin={0} onClick={isEditing ? () => setIsEditStepName(true) : undefined}>
                      {name}
                    </Text>
                  )}
                </Box>
              )}

              {searchText && isCollapsed && searchIndex > -1 && (
                <VStack>
                  <Highlighter
                    class={cx(styles.title)}
                    highlightClassName={styles.highlight}
                    searchWords={[
                      ...String(searchText).toUpperCase().split(' '),
                      ...String(searchText).toLowerCase().split(' ')
                    ]}
                    autoEscape
                    textToHighlight={textToHighlight}
                  />
                </VStack>
              )}
            </div>
          </div>

          {canEditProcess && !isProcessArchived && (
            <div className={styles.editGroup}>
              {isEditing ? (
                <>
                  <Button
                    gap={{ base: 0, md: 2 }}
                    width={{ base: 10, md: 'initial' }}
                    paddingX={{ base: 0, md: 4 }}
                    variant="outline"
                    background="white"
                    borderRadius="6px"
                    borderColor="red"
                    color="red"
                    fontWeight={500}
                    fontSize={{ base: 0, md: '16px' }}
                    lineHeight="24px"
                    marginTop="8px"
                    className={styles.deleteButton}
                    _hover={{ background: 'red.500', color: 'white' }}
                    _active={{ background: 'red.500', color: 'white' }}
                    onClick={handleDeleteStep}
                    disabled={lastStep}
                  >
                    <SvgIcon iconName="trash-red" size={16} />
                    Delete
                  </Button>

                  <Button
                    gap={{ base: 0, md: 2 }}
                    width={{ base: 10, md: 'initial' }}
                    paddingX={{ base: 0, md: 4 }}
                    variant="outline"
                    borderRadius="6px"
                    color="white"
                    fontWeight={500}
                    margin="8px"
                    marginRight="0px"
                    fontSize={{ base: 0, md: '16px' }}
                    lineHeight="24px"
                    background={currentTheme?.primaryColor ?? 'primary.500'}
                    _hover={{
                      background: currentTheme?.primaryColor ?? 'primary.700',
                      opacity: currentTheme?.primaryColor ? 0.8 : 1
                    }}
                    _active={{
                      background: currentTheme?.primaryColor ?? 'primary.700',
                      opacity: currentTheme?.primaryColor ? 0.8 : 1
                    }}
                    _focus={{
                      background: currentTheme?.primaryColor ?? 'primary.700',
                      opacity: currentTheme?.primaryColor ? 0.8 : 1
                    }}
                    onClick={e => {
                      e.stopPropagation()
                      onStopEditing(e)
                      handleShowAutosave()
                    }}
                  >
                    <SvgIcon iconName="save_ic" size={16} />
                    Done
                  </Button>
                </>
              ) : (
                <>
                  {!isBasicUser && (
                    <Tooltip
                      label="Note"
                      height="36px"
                      fontSize="14px"
                      padding={2}
                      background="gray.700"
                      placement="top"
                      color="white"
                      hasArrow
                      borderRadius="4px"
                    >
                      <IconButton
                        variant="outline"
                        colorScheme="transparent"
                        aria-label="Comment"
                        borderRadius="6px"
                        border="none"
                        icon={<SvgIcon iconName="ic_outline-forum" color={colors.gray[600]} size={17} />}
                        onClick={e => {
                          e.stopPropagation()
                          onToggleMessageView(true)
                        }}
                      />
                    </Tooltip>
                  )}
                  {isManageMode && !isCommonStepFromStepId && (
                    <>
                      <Tooltip
                        label="Edit"
                        height="36px"
                        fontSize="14px"
                        padding={2}
                        background="gray.700"
                        placement="top"
                        color="white"
                        hasArrow
                        borderRadius="4px"
                      >
                        <IconButton
                          variant="outline"
                          colorScheme="transparent"
                          aria-label="Edit"
                          borderRadius="6px"
                          border="none"
                          icon={<SvgIcon iconName="ic_baseline-edit" color={colors.gray[600]} size={15} />}
                          onClick={e => {
                            e.stopPropagation()
                            onStartEditing(e)
                          }}
                        />
                      </Tooltip>
                      {!step?.commonLibrary &&
                        processStore.process?.isPublished &&
                        !isBasicUser &&
                        commonLibraryStore?.isFeatureEnabled && (
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              className={styles.menuIcon}
                              aria-label="Options"
                              onClick={e => e.stopPropagation()}
                              icon={<SvgIcon size={16} iconName="show-more-icon" />}
                            />
                            <MenuList className={styles.menuList}>
                              <MenuItem
                                icon={<SvgIcon size={16} iconName="doc-add" />}
                                onClick={async event => {
                                  event.stopPropagation()
                                  try {
                                    await addStepToCommonLibrary({
                                      processId: procedureId,
                                      organizationId: organizationStore.organization?.id,
                                      stepId: step?.id
                                    })
                                    getRenderProcess(processStore.process.id, processStore)
                                    toast.success('Add drill to common library successfully')
                                  } catch (error: any) {
                                    toast.error(`Something wrong happened: ${error}`)
                                  }
                                }}
                              >
                                Add drill to common library
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        )}
                    </>
                  )}

                  {isManageMode && !!isCommonStepFromStepId && (
                    <Tooltip
                      label="Remove"
                      height="36px"
                      fontSize="14px"
                      padding={2}
                      background="gray.700"
                      placement="top"
                      color="white"
                      hasArrow
                      borderRadius="4px"
                    >
                      <IconButton
                        variant="outline"
                        colorScheme="transparent"
                        aria-label="Remove drill"
                        borderRadius="6px"
                        border="none"
                        icon={<SvgIcon iconName="ic_delete" color={colors.gray[600]} size={20} />}
                        onClick={e => {
                          e.stopPropagation()
                          handleRemoveCommonStep(e)
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}
        </HStack>
        {isMobile && (
          <Box
            onClick={
              isEditing
                ? event => {
                    event.stopPropagation()
                    setIsEditStepName(true)
                  }
                : undefined
            }
            flexDirection="row"
            display="flex"
            paddingBottom={2}
          >
            {isEditStepName && isEditing ? (
              <InlineTextField
                value={name}
                onApplyChange={handleEditStepName}
                editFirst
                onStartEdit={() => setIsEditStepName(true)}
                onStopEdit={() => setIsEditStepName(false)}
              />
            ) : (
              <Text
                color="gray.700"
                fontWeight={500}
                lineHeight={5}
                margin={0}
                onClick={isEditing ? () => setIsEditStepName(true) : undefined}
              >
                {!hideStepNumbers ? `${position}. ${name}` : name}
              </Text>
            )}
          </Box>
        )}
      </VStack>
      {isStepLevelChange && stepNeedsReview && (
        <Box position="absolute" bottom="6px" right="6px" zIndex={10}>
          <Button
            onClick={handleReviewButtonClick}
            size="md"
            minWidth="100px"
            height="36px"
            borderRadius="6px"
            fontWeight={500}
            fontSize="15px"
            bg={stepIsReviewed ? 'green.500' : 'white'}
            color={stepIsReviewed ? 'white' : 'red.500'}
            border={stepIsReviewed ? 'none' : '2px solid'}
            borderColor={stepIsReviewed ? 'transparent' : 'red.500'}
            leftIcon={stepIsReviewed ? <CheckIcon /> : undefined}
            _hover={{
              bg: stepIsReviewed ? 'green.600' : 'red.50',
              borderColor: stepIsReviewed ? 'transparent' : 'red.500'
            }}
            _active={{
              bg: stepIsReviewed ? 'green.700' : 'red.100'
            }}
          >
            {stepIsReviewed ? 'Reviewed' : 'Review'}
          </Button>
        </Box>
      )}
      </Col>
      <MediaManager
        onUpdateCaption={handleChangeCaption}
        onFileUpload={uploadMedia}
        className={styles.mediaDialogprops}
        toggle={() => setShowManageMediaDialog(!showManageMediaDialog)}
        isOpen={showManageMediaDialog}
        mediaList={props.mediaList}
        handleShowAutosave={() => handleShowAutosave()}
      />
      <ModalConfirm
        title={step.commonLibrary ? 'Delete drill?' : 'Remove drill?'}
        content={
          step.commonLibrary
            ? 'Are you sure you want to delete this Common drill? All processes using this will be affected.'
            : 'Are you sure you want to remove this drill? This action can not be undo.'
        }
        isOpen={deleteStepId > 0}
        confirmAction="Remove"
        toggle={() => setDeleteStepId(0)}
        isDelete
        onAccept={handleDeleteStepConfirmed}
      />
      <ModalAddStep
        isOpen={showModalAddStep}
        onClose={() => setShowModalAddStep(false)}
        procedureId={procedureId}
        handleShowAutosave={() => handleShowAutosave()}
      />
      <SelectIconModal isOpen={isOpen} onClose={onClose} onSelect={onSelect} />
    </Box>
  )
}

export default observer(CardHeader)
